import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import { supabase } from '../lib/supabase';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !email.includes('@')) {
      setErrorMessage('Please enter a valid email address');
      setStatus('error');
      return;
    }

    setIsSubmitting(true);
    setStatus('idle');
    setErrorMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });

      if (error) {
        console.error('Password reset error:', error);
        setErrorMessage(error.message || 'Failed to send reset email. Please try again.');
        setStatus('error');
        return;
      }

      setStatus('success');
    } catch (err) {
      console.error('Unexpected error during password reset:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusContent = () => {
    switch (status) {
      case 'success':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M20 32L28 40L44 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Reset Email Sent!',
          message: `We've sent a password reset link to ${email}. Please check your email and follow the instructions.`,
          color: '#177508'
        };
      
      case 'error':
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#C9121A"/>
              <path d="M20 20L44 44M44 20L20 44" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          ),
          title: 'Reset Failed',
          message: errorMessage,
          color: '#C9121A'
        };
      
      default:
        return {
          icon: (
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="32" cy="32" r="32" fill="#177508"/>
              <path d="M32 8C18.745 8 8 18.745 8 32s10.745 24 24 24 24-10.745 24-24S45.255 8 32 8zm0 44c-11.046 0-20-8.954-20-20s8.954-20 20-20 20 8.954 20 20-8.954 20-20 20z" fill="white"/>
              <path d="M32 16c-8.837 0-16 7.163-16 16s7.163 16 16 16 16-7.163 16-16-7.163-16-16-16zm0 28c-6.627 0-12-5.373-12-12s5.373-12 12-12 12 5.373 12 12-5.373 12-12 12z" fill="white"/>
            </svg>
          ),
          title: 'Reset Your Password',
          message: 'Enter your email address and we\'ll send you a link to reset your password.',
          color: '#177508'
        };
    }
  };

  const content = getStatusContent();

  if (status === 'success') {
    return (
      <div className='verification-container'>
        <div className='verification-content'>
          <div className='verification-card'>
            <div className='verification-icon'>
              {content.icon}
            </div>
            
            <h1 className='titles large color black'>{content.title}</h1>
            
            <p className='paragraphs medium color charcoal'>
              {content.message}
            </p>
            
            <div className='verification-actions'>
              <button 
                className='btn primary'
                onClick={() => navigate('/')}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    );
  }

  return (
    <div className='verification-container'>
      <div className='verification-content'>
        <div className='verification-card'>
          <div className='verification-icon'>
            {content.icon}
          </div>
          
          <h1 className='titles large color black'>{content.title}</h1>
          
          <p className='paragraphs medium color charcoal'>
            {content.message}
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className='form-group'>
              <label>
                <span className='labels large color black'>Email address <span className='required'>*</span></span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder='Your email address'
                  className='paragraphs medium color black'
                  disabled={isSubmitting}
                />
              </label>
            </div>
            
            {status === 'error' && (
              <div className="error-message general-error">
                {errorMessage}
              </div>
            )}
            
            <div className='verification-actions'>
              <button 
                type="submit"
                className='btn primary'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Reset Link'}
              </button>
              <button 
                type="button"
                className='btn secondary'
                onClick={() => navigate('/')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
          
          {isSubmitting && (
            <div className='loading-spinner'>
              <div className='spinner'></div>
            </div>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ForgotPassword; 